<template>
  <proa-modal
    :modal="unusable"
    :title="$t('common.modals.unusableModal.title')"
    hideCloseButton
  >
    <template v-if="initialized">
      <div class="mb-3">
        <span>{{ $t('common.modals.unusableModal.messages.main') }}</span>
      </div>
      <!-- NOTE: クレカ変更済みの場合にはAdminもSwitchable判定に流すため単独のifで判定 -->
      <div v-if="waitingForLicenseRecovery">
        <span class="text-secondary text-pre-wrap d-block mb-2">{{ $t('common.modals.unusableModal.messages.waitingForLicenseRecovery') }}</span>
        <div class="text-secondary mb-2">
          <span>お急ぎの方は</span>
          <span class="contact-link" @click="toServiceContact">こちら</span>
          <span>からお問い合わせください。</span>
        </div>
      </div>
      <!-- NOTE: クレカ変更が必要な場合にAdminにはクレカ変更機能を強制提供する -->
      <div v-if="paymentSystemUpdateRequired">
        <span class="text-secondary d-block mb-2">{{ $t('common.modals.unusableModal.messages.paymentSystemUpdateRequired') }}</span>
      </div>
      <div v-else-if="organizationSwitchable">
        <span class="text-secondary d-block mb-2">{{ $t('common.modals.unusableModal.messages.changeOrganization') }}</span>
        <proa-select
          v-model="changeOrganizationId"
          :options="organizationOptions"
        />
      </div>
      <div v-else-if="organizationCreatable">{{ $t('common.modals.unusableModal.messages.createOrJoinOrganization') }}</div>
      <template slot="footer">
        <proa-button
          v-if="paymentSystemUpdateRequired"
          @click.native="editPaymentSystemModalWithObject = paymentSystem"
          class="default-button-width mr-2"
          variant="primary"
        >
          {{ $t('common.modals.unusableModal.messages.creditCardReregistration') }}
        </proa-button>
        <proa-button
          v-else
          @click.native="signOutModal = true"
          class="default-button-width mr-2"
          variant="light"
        >
          {{ $t('common.signOut') }}
        </proa-button>
        <proa-button
          v-if="!paymentSystemUpdateRequired && organizationSwitchable"
          @click.native="onClickChangeOrganization"
          class="default-button-width"
          variant="primary"
        >
          {{ $t('common.ok') }}
        </proa-button>
        <proa-button
          v-else-if="organizationCreatable"
          @click.native="createNewOrganizationModal = true"
          class="default-button-width"
          variant="primary"
        >
          {{ $t('common.createNewOrganization') }}
        </proa-button>
      </template>
      <proa-modal
        v-if="signedInUser"
        :modal="signOutModal"
        :title="$t('common.signOut')"
        @close="signOutModal = false"
      >
        <div class="mb-4">{{ $t('common.signOutConfirmMessage') }}</div>
        <div class="font-weight-w6 text-secondary mb-4">{{ $t('common.currentSignedInAccount') }}</div>
        <div class="d-flex flex-md-row flex-column align-items-center">
          <proa-avatar :src="signedInUser.meta.avatar || require('@/assets/images/avatar_default.png')"/>
          <div class="mt-2 mt-md-0 ml-md-4 d-flex flex-column justify-content-center align-items-center align-items-md-start">
            <div class="mb-1 font-weight-w6">{{ signedInUser.meta.displayName }}</div>
            <div>{{ signedInUser.email }}</div>
          </div>
        </div>
        <template slot="footer">
          <proa-button
            @click.native="signOutModal = false"
            class="default-button-width mr-2"
          >
            {{ $t('common.cancel') }}
          </proa-button>
          <proa-button
            @click.native="onClickConfirmedSignOut"
            variant="secondary"
            class="default-button-width"
          >
            {{ $t('common.ok') }}
          </proa-button>
        </template>
      </proa-modal>
      <validation-observer v-slot="{ handleSubmit }">
        <form novalidate @submit.prevent="() => { newOrganizationValidated = true; handleSubmit(onSubmitCreateNewOrganization) }">
          <proa-modal
            :modal="createNewOrganizationModal"
            :title="$t('common.createNewOrganization')"
            @close="closeCreateNewOrganizationModal"
          >
            <div class="mb-4">{{ $t('common.modals.createNewOrganizationModal.message') }}</div>
            <validation-provider
              :name="$t('models.organization.attributes.name')"
              rules="required|max:50"
              v-slot="{ errors }"
            >
              <proa-input-group :invalidFeedback="newOrganizationValidated ? invalidOrganizationName ? $t('pages.confirmation.invalidOrganizationName') : errors[0] : null" class="mb-0">
                <template slot="input">
                  <proa-input
                    v-model="newOrganizationName"
                    type="text"
                    :placeholder="$t('models.organization.attributes.name')"
                    :disabled="createNewOrganizationRequesting"
                    dark
                  />
                </template>
              </proa-input-group>
            </validation-provider>
            <template slot="footer">
              <proa-button
                :disabled="createNewOrganizationRequesting"
                @click.native="closeCreateNewOrganizationModal"
                class="mr-2"
              >
                {{ $t('common.cancel') }}
              </proa-button>
              <proa-button
                :loading="createNewOrganizationRequesting"
                type="submit"
                variant="primary"
              >
                {{ $t('common.create') }}
              </proa-button>
            </template>
          </proa-modal>
        </form>
      </validation-observer>
      <proa-modal
        :modal="createdNewOrganizationModal"
        :title="$t('common.modals.createdNewOrganizationModal.title')"
        @close="closeCreatedNewOrganizationModal"
        hideCloseButton
      >
        <div class="text-pre-wrap">
          <span>{{ $t('common.modals.createdNewOrganizationModal.message') }}</span>
        </div>
      </proa-modal>
      <edit-payment-method-modal
        :modal="editPaymentSystemModalWithObject != null"
        :paymentSystem="editPaymentSystemModalWithObject"
        :paymentSystemMaster="paymentSystemMaster"
        :requesting="updatePaymentSystemRequesting"
        :title="$t('common.modals.unusableModal.messages.creditCardReregistration')"
        @close="editPaymentSystemModalWithObject = null"
        @createdPaymentToken="onCreatedPaymentToken"
      />
    </template>
  </proa-modal>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

import Organization from '@/store/models/Organization'
import PaymentSystem from '@/store/models/admin/PaymentSystem'
import PaymentSystemMaster from '@/store/models/admin/PaymentSystemMaster'

import EditPaymentMethodModal from '@/components/molecules/management/paymentMethod/EditModal'

export default {
  name: 'UnusableOrganizationModal',
  components: {
    EditPaymentMethodModal
  },
  computed: {
    organizations: function() { return Organization.all() },
    organizationOptions: function() {
      return this.organizations.map(organization => {
        return {
          label: organization.isActivated ? organization.name : organization.name + '（この組織は現在利用できません）',
          value: organization.id,
          selected: this.currentOrganization && organization.isActivated && organization.id == this.currentOrganization.id,
          disabled: !organization.isActivated
        }
      })
    },
    organizationSwitchable: function() {
      return this.organizations.filter(organization => {
        return organization.isActivated
      }).length > 0
    },
    organizationCreatable: function() {
      return this.organizations.filter(organization => {
        return organization.adminUserId == this.signedInUser.id
      }).length == 0
    },
    paymentSystem: function() {
      return PaymentSystem.query().first()
    },
    paymentSystemMaster: function() {
      return PaymentSystemMaster.query().first()
    },
    // NOTE: createdでAPIリクエストをAdminに限定しているため条件分岐に入れる必要はなし
    paymentSystemUpdateRequired: function() {
      return this.paymentSystemMaster != null &&
             this.paymentSystem?.status == 'errorDetect'
    },
    waitingForLicenseRecovery: function() {
      return this.paymentSystemMaster != null &&
             this.paymentSystem?.status == 'usePay'
    },
    ...mapGetters({
      httpError: 'httpError',
      currentOrganization: 'auth/currentOrganization',
      signedInUser: 'auth/signedInUser',
      unusable: 'unusable'
    })
  },
  data() {
    return {
      changeOrganizationId: null,
      createNewOrganizationModal: false,
      createdNewOrganizationModal: false,
      createNewOrganizationRequesting: false,
      editPaymentSystemModalWithObject: null,
      initialized: false,
      invalidOrganizationName: false,
      newOrganizationName: '',
      newOrganizationValidated: false,
      signOutModal: false,
      updatePaymentSystemRequesting: false
    }
  },
  methods: {
    async onClickChangeOrganization() {
      await this.apiPutAuth({
        defaultOrganizationId: this.changeOrganizationId
      })
      window.location = '/'
    },
    async onSubmitCreateNewOrganization() {
      this.createNewOrganizationRequesting = true
      await this.apiPostOrganization({
        name: this.newOrganizationName
      })
      if (this.httpError) {
        if (this.httpError.response?.data?.errors?.name) this.invalidOrganizationName = true
      } else {
        this.createdNewOrganizationModal = true
      }
      this.createNewOrganizationRequesting = false
    },
    onClickConfirmedSignOut: function() {
      this.signOut()
    },
    closeCreateNewOrganizationModal: function() {
      if (this.createNewOrganizationRequesting) return
      this.createNewOrganizationModal = this.invalidOrganizationName = false
      this.newOrganizationName = ''
    },
    closeCreatedNewOrganizationModal: function() {
      location.href = '/'
    },
    async onCreatedPaymentToken(params) {
      this.updatePaymentSystemRequesting = true
      await this.apiPutPaymentSystem({
        id: this.paymentSystem.id,
        attributes: params
      })

      if (!this.httpError) {
        location.href = '/'
      }
    },
    toServiceContact: function() {
      location.href = '/service/contact'
    },
    ...mapActions({
      signOut: 'auth/signOut',
      apiGetPaymentSystems: 'entities/paymentSystems/index',
      apiGetPaymentSystemMasters: 'entities/paymentSystemMasters/index',
      apiPutAuth: 'auth/update',
      apiPutPaymentSystem: 'entities/paymentSystems/update',
      apiPostOrganization: 'entities/organizations/create'
    })
  },
  async created() {
    if (this.currentOrganization?.adminUserId == this.signedInUser.id) {
      await this.apiGetPaymentSystemMasters()
      await this.apiGetPaymentSystems()
    }
    this.initialized = true
  }
}
</script>

<style scoped lang="scss">
@import "@/styles/variables";

.contact-link {
  color: $link-color;
  cursor: pointer;
}
</style>
